/**
 * Edit document metadata modal - admin/team only (CHAP-97).
 *
 * Edits: name, description, sensitivityTier, provenanceTier, category,
 * sourceAttribution, isPrimary.
 *
 * Per CHAP-97 ACs:
 *   - Promotion to 'authoritative' requires admin role (server enforces)
 *   - Demotion FROM 'authoritative' to superseded/archived requires
 *     a reason >= 10 chars (collected via the reason field; server
 *     enforces)
 *   - Sensitivity tier change shows a confirmation modal noting the
 *     data-exposure implication (clients can see who can see what)
 */

import { useState, useEffect } from 'react';
import { useEditDocument } from '../../hooks/useDocuments.js';
import type {
  DataroomDocument,
  SensitivityTier,
  ProvenanceTier,
  IntakeCategory,
} from '../../../shared/types.js';
import { ApiError } from '../../lib/api.js';

interface Props {
  slug: string;
  doc: DataroomDocument;
  onClose: () => void;
  onSaved: (updated: DataroomDocument) => void;
  /** True if the current user has admin role (vs. team). Promotion to authoritative is admin-only. */
  isAdmin: boolean;
}

const SENSITIVITY_LABELS: Record<SensitivityTier, string> = {
  public: 'Public',
  restricted: 'Restricted',
  confidential: 'Confidential',
  highly_confidential: 'Highly confidential',
};

const PROVENANCE_LABELS: Record<ProvenanceTier, string> = {
  authoritative: 'Authoritative',
  working_draft: 'Working draft',
  informal_note: 'Informal note',
  scratch: 'Scratch',
  superseded: 'Superseded',
  archived: 'Archived',
};

const CATEGORY_LABELS: Record<IntakeCategory, string> = {
  financial: 'Financial',
  strategic: 'Strategic',
  sentiment: 'Sentiment',
  architectural: 'Architectural',
  legal: 'Legal',
  member_list: 'Member list',
  recording: 'Recording',
  transcript: 'Transcript',
  contract: 'Contract',
  image: 'Image',
  other: 'Other',
};

export default function EditDocumentModal({
  slug,
  doc,
  onClose,
  onSaved,
  isAdmin,
}: Props) {
  const [name, setName] = useState(doc.name);
  const [description, setDescription] = useState(doc.description ?? '');
  const [sensitivity, setSensitivity] = useState<SensitivityTier>(doc.sensitivityTier);
  const [provenance, setProvenance] = useState<ProvenanceTier>(doc.provenanceTier);
  const [category, setCategory] = useState<IntakeCategory | ''>(doc.category ?? '');
  const [sourceAttribution, setSourceAttribution] = useState(
    doc.sourceAttribution ?? '',
  );
  const [isPrimary, setIsPrimary] = useState(doc.isPrimary);
  const [reason, setReason] = useState('');

  const [confirmingSensitivity, setConfirmingSensitivity] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const edit = useEditDocument(slug);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sensitivityChanged = sensitivity !== doc.sensitivityTier;
  const promotingToAuthoritative =
    provenance === 'authoritative' && doc.provenanceTier !== 'authoritative';
  const demotingFromAuthoritative =
    doc.provenanceTier === 'authoritative' &&
    (provenance === 'superseded' || provenance === 'archived');
  const reasonValid = !demotingFromAuthoritative || reason.trim().length >= 10;

  async function doSave() {
    setError(null);
    try {
      const result = await edit.mutateAsync({
        docId: doc.id,
        edits: {
          name: name !== doc.name ? name : undefined,
          description: description !== (doc.description ?? '') ? description : undefined,
          sensitivityTier: sensitivity !== doc.sensitivityTier ? sensitivity : undefined,
          provenanceTier: provenance !== doc.provenanceTier ? provenance : undefined,
          category:
            category !== (doc.category ?? '')
              ? ((category || null) as IntakeCategory | null) ?? undefined
              : undefined,
          sourceAttribution:
            sourceAttribution !== (doc.sourceAttribution ?? '')
              ? sourceAttribution
              : undefined,
          isPrimary: isPrimary !== doc.isPrimary ? isPrimary : undefined,
          ...(demotingFromAuthoritative ? { reason } : {}),
        },
      });
      onSaved(result.document);
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status}: ${
              typeof err.body === 'object' && err.body !== null && 'message' in err.body
                ? (err.body as { message: string }).message
                : err.message
            }`
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    }
  }

  function attemptSave() {
    setError(null);
    if (promotingToAuthoritative && !isAdmin) {
      setError("Only admin role can promote to 'authoritative'.");
      return;
    }
    if (demotingFromAuthoritative && !reasonValid) {
      setError(
        "Demoting from 'authoritative' requires a reason of at least 10 characters.",
      );
      return;
    }
    if (sensitivityChanged) {
      // Show confirmation modal first
      setConfirmingSensitivity(true);
      return;
    }
    void doSave();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-doc-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3,36,77,0.4)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4 sm:p-6 space-y-4 min-w-0">
          <header className="flex items-start justify-between gap-3 min-w-0">
            <div className="flex-1 min-w-0">
              <h2 id="edit-doc-title" className="text-navy text-lg font-bold">
                Edit document metadata
              </h2>
              <p className="text-xs text-neutral truncate">
                {doc.filename}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral hover:text-navy p-1"
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <div className="space-y-3">
            <Field label="Display name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
              <Field label="Sensitivity tier">
                <select
                  value={sensitivity}
                  onChange={(e) => setSensitivity(e.target.value as SensitivityTier)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Object.entries(SENSITIVITY_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Provenance tier">
                <select
                  value={provenance}
                  onChange={(e) => setProvenance(e.target.value as ProvenanceTier)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Object.entries(PROVENANCE_LABELS).map(([v, l]) => {
                    const isAuth = v === 'authoritative';
                    return (
                      <option
                        key={v}
                        value={v}
                        disabled={isAuth && !isAdmin && doc.provenanceTier !== 'authoritative'}
                      >
                        {l}
                        {isAuth && !isAdmin && doc.provenanceTier !== 'authoritative'
                          ? ' (admin only)'
                          : ''}
                      </option>
                    );
                  })}
                </select>
              </Field>

              <Field label="Category">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as IntakeCategory | '')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">(none)</option>
                  {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Source attribution">
                <input
                  type="text"
                  value={sourceAttribution}
                  onChange={(e) => setSourceAttribution(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-navy">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
              />
              Canonical / primary version of this document
            </label>

            {demotingFromAuthoritative && (
              <Field label={`Reason for demotion (>= 10 chars, required)`}>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="w-full border border-orange-300 rounded px-3 py-2 text-sm"
                  placeholder="e.g. Superseded by 2026-09-15 FINAL version"
                />
                <p
                  className={`text-xs mt-1 ${
                    reasonValid ? 'text-neutral' : 'text-red-600'
                  }`}
                >
                  {reason.trim().length} / 10 chars
                </p>
              </Field>
            )}

            {error && (
              <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2">
                {error}
              </div>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              disabled={edit.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={attemptSave}
              disabled={edit.isPending}
              className="bg-navy text-white text-sm font-medium px-4 py-2 rounded hover:bg-navy/90 disabled:opacity-50"
            >
              {edit.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </footer>
        </div>

        {confirmingSensitivity && (
          <SensitivityConfirmModal
            from={doc.sensitivityTier}
            to={sensitivity}
            onConfirm={() => {
              setConfirmingSensitivity(false);
              void doSave();
            }}
            onCancel={() => setConfirmingSensitivity(false)}
          />
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-navy mb-1">{label}</span>
      {children}
    </label>
  );
}

function SensitivityConfirmModal({
  from,
  to,
  onConfirm,
  onCancel,
}: {
  from: SensitivityTier;
  to: SensitivityTier;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Compute the change direction so we can phrase the warning correctly
  const order: SensitivityTier[] = [
    'public',
    'restricted',
    'confidential',
    'highly_confidential',
  ];
  const fromIdx = order.indexOf(from);
  const toIdx = order.indexOf(to);
  const direction = toIdx < fromIdx ? 'loosening' : 'tightening';

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3,36,77,0.6)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <h3 className="text-navy text-lg font-bold">
          {direction === 'loosening' ? 'Loosening access' : 'Tightening access'}
        </h3>
        <p className="text-sm text-navy">
          Changing sensitivity from <strong>{SENSITIVITY_LABELS[from]}</strong> to{' '}
          <strong>{SENSITIVITY_LABELS[to]}</strong>.
        </p>
        <p className="text-sm text-neutral">
          {direction === 'loosening'
            ? "More members will now be able to see this document. If it contains donor capacity, financial details, or sentiment, this could expose data to people who shouldn't see it."
            : 'Fewer members will be able to see this document. Make sure anyone who currently needs access has a role/tier that still qualifies.'}
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-medium px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-navy text-white text-sm font-medium px-4 py-2 rounded hover:bg-navy/90"
          >
            Confirm change
          </button>
        </div>
      </div>
    </div>
  );
}
